import { v4 as uuidv4 } from 'uuid';
import { Form, STANDARD_FORM_TYPES } from './Form';
import { DateTimeQuestion, SelectByFeatureQuestion } from './Question';
import { normalizeFeatureActionValue } from './featureActionValue';

export class FormResponse {
  constructor({
    userId,
    resortId,
    formId,
    formType,
    featureIds,
    trailIds,
    spatialFeatureIds,
    recordedTimestamp,
    timestamp,
    sport,
    completedForm,
    author,
  }) {
    if (!userId) {
      throw new Error('missing "userId" when creating form response');
    }
    if (!resortId) {
      throw new Error('missing "resortId" when creating form response');
    }
    if (!formId) {
      throw new Error('missing "formId" when creating form response');
    }
    if (!formType) {
      throw new Error('missing "formType" when creating form response');
    }
    if (!completedForm) {
      throw new Error('missing "completedForm" when creating form response');
    }
    this.id = uuidv4();
    this.userId = userId;
    this.resortId = resortId;
    this.formId = formId;
    this.formType = formType;
    this.featureIds = featureIds || [];
    this.trailIds = trailIds || [];
    this.spatialFeatureIds = spatialFeatureIds || [];
    this.recordedTimestamp = recordedTimestamp; // || Date.now();
    this.timestamp = timestamp || Date.now();
    this.sport = sport;
    this.completedForm = completedForm;
    // Read-only `{ userName, name }` the server joins onto single-response GETs
    // (getFormResponseWithAuthor). Kept so the response page header can show it;
    // the write handlers ignore it.
    this.author = author;
  }

  clone() {
    const formResponse = new this.constructor({...this});
    formResponse.id = this.id;
    return formResponse;
  }

  setDefaultValuesForDateTimeQuestions(timestamp = Date.now()) {
    for (const questionSet of this.completedForm.questionSets) {
      for (const question of questionSet.questions) {
        if (question.questionType === DateTimeQuestion.questionType() && !question.value) {
          question.value = timestamp;
        }
      }
    }
  }

  // Locate the park-log "recorded time" date/time question. Park-log forms carry a
  // required dateTime question whose `field` is 'timestamp' — the backend historically
  // read it to set the park log's recorded time. We keep it in sync with the form
  // response's own `timestamp` (the clock control in the form-response modal). Falls back
  // to the first required dateTime question if no `field === 'timestamp'` question exists.
  getParkLogTimestampQuestion() {
    if (this.formType !== STANDARD_FORM_TYPES.PARK_LOG_FORM) {
      return undefined;
    }
    let fallback;
    for (const questionSet of this.completedForm?.questionSets || []) {
      for (const question of questionSet?.questions || []) {
        if (question?.questionType !== DateTimeQuestion.questionType()) {
          continue;
        }
        if (question.field === 'timestamp') {
          return question;
        }
        if (question.required && !fallback) {
          fallback = question;
        }
      }
    }
    return fallback;
  }

  // For park-log forms, the clock control's `timestamp` is the source of truth for the
  // required date/time question. Force the question to match so its value agrees with the
  // submitted time even if neither control was touched. No-op for non-park-log forms.
  syncParkLogTimestampQuestion() {
    const timestampQuestion = this.getParkLogTimestampQuestion();
    if (timestampQuestion) {
      timestampQuestion.value = this.timestamp;
    }
  }

  // A `selectByFeature` answer is persisted as a flat map keyed by entityId for
  // park logs (the backend force-normalizes park-log writes to that shape — see
  // shared `_backfillParkLogFeatureKeyset`), but the canonical wire/edit shape is
  // an array `[{ entityType, entityId, value }]`. Normalizing on load means the
  // whole app sees the array shape and reads don't crash on the stored map (the
  // array-shape UI `FeatureActionInput` renders it). entityType is classified
  // from the response-level id lists, mirroring the server read path
  // (`formResponseToParkLog`).
  normalizeFeatureActionValues() {
    const entityIds = {
      featureIds: this.featureIds,
      trailIds: this.trailIds,
      spatialFeatureIds: this.spatialFeatureIds,
    };
    for (const questionSet of this.completedForm?.questionSets || []) {
      for (const question of questionSet?.questions || []) {
        if (question?.questionType === SelectByFeatureQuestion.questionType()) {
          question.value = normalizeFeatureActionValue(question.value, entityIds);
        }
      }
    }
  }

  static factory(rawObject) {
    const constructedFormResponse = new FormResponse({
      ...rawObject,
      completedForm: Form.factory(rawObject.completedForm),
    });
    if (rawObject.id) {
      constructedFormResponse.id = rawObject.id;
    }
    // Read-side fields the constructor doesn't take: who last edited it
    // (ULLR-183: `userId` stays the author) and the task it is attached to.
    // The update route builds its row from named fields, so carrying them is
    // harmless on a save. (`author` goes through the constructor.)
    if (rawObject.updatedBy) {
      constructedFormResponse.updatedBy = rawObject.updatedBy;
    }
    if (rawObject.calendarComponentId) {
      constructedFormResponse.calendarComponentId = rawObject.calendarComponentId;
    }
    constructedFormResponse.normalizeFeatureActionValues();
    return constructedFormResponse;
  }
}
